import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, IndianRupee, Briefcase, Calendar, Building2, Users, Star, Share2, Flag, Bookmark, MessageCircle } from "lucide-react";
import StarRating from "@/components/StarRating";
import JobCard from "@/components/JobCard";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [cover, setCover] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [applied, setApplied] = useState(false);
  const [appliedStatus, setAppliedStatus] = useState("");
  const [profilePct, setProfilePct] = useState(100);
  const [companyJobs, setCompanyJobs] = useState([]);

  useEffect(() => {
    api.get(`/jobs/${id}`).then(r => {
      setJob(r.data);
      // track view (fire and forget)
      api.post(`/jobs/${id}/view`).catch(() => {});
      // other jobs by same company in same category
      if (r.data.company_id) {
        api.get(`/companies/${r.data.company_id}/jobs`, {
          params: { category: r.data.category, exclude_job_id: id }
        }).then(rr => setCompanyJobs(rr.data));
      }
    }).catch(() => navigate("/jobs"));
  }, [id, navigate]);

  useEffect(() => {
    if (user && user.role === "job_seeker") {
      api.get("/seeker/applied-job-ids").then(r => {
        if (r.data.ids?.includes(id)) {
          setApplied(true);
          setAppliedStatus(r.data.statuses?.[id] || "viewed");
        }
      });
      api.get("/seeker/profile-completion").then(r => setProfilePct(r.data.percent));
    }
  }, [user, id]);

  if (!job) return <div className="max-w-7xl mx-auto px-4 py-16"><div className="skeleton h-64 rounded-xl" /></div>;

  const salary = (() => {
  if (job.salary_type === "hidden" || (!job.salary_type && !job.show_salary)) return "Not Disclosed";
  if (job.salary_type === "yearly")
    return `${job.salary_min || 0} – ${job.salary_max || 0} LPA`;
  return `₹${(job.salary_min || 0).toLocaleString()} – ₹${(job.salary_max || 0).toLocaleString()} /month`;
  })();

  const apply = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "job_seeker") { toast.error("Only job seekers can apply"); return; }
    if (profilePct < 75) {
      toast.error(`Complete at least 75% of your profile to apply (currently ${profilePct}%)`);
      navigate("/dashboard/seeker");
      return;
    }
    try {
      await api.post("/applications", { job_id: job.id, cover_note: cover });
      toast.success("Application submitted!");
      setApplied(true);
      setAppliedStatus("viewed");
      setOpen(false);
    } catch (e) { toast.error(formatError(e)); }
  };

  const save = async () => {
    if (!user) { navigate("/login"); return; }
    try { await api.post(`/saved-jobs/${job.id}`); toast.success("Saved to your list"); }
    catch (e) { toast.error(formatError(e)); }
  };

  const submitReport = async () => {
    try {
      const fd = new FormData();
      fd.append("reason", reportReason);
      await api.post(`/jobs/${job.id}/report`, fd);
      toast.success("Reported. Thanks!");
      setReportOpen(false);
    } catch (e) { toast.error(formatError(e)); }
  };

  const whatsappLink = job.apply_method === "whatsapp" && job.apply_contact
    ? `https://wa.me/${job.apply_contact.replace(/\D/g, "")}?text=Hi%2C%20I%20saw%20your%20%22${encodeURIComponent(job.title)}%22%20listing%20on%20Job%20Dew.`
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="job-detail-page">
      <Link to="/jobs" className="text-sm text-[#1D6FF2] mb-4 inline-block">← Back to jobs</Link>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 md:p-8">
          <div className="flex items-center gap-2 flex-wrap">
            {job.featured && <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Star className="w-3 h-3 mr-1 fill-amber-500" /> Featured</Badge>}
            <Badge className="badge-category">{job.category}</Badge>
            <Badge variant="outline">{job.job_type}</Badge>
            {job.job_type === "Walk-in" && <Badge className="bg-orange-100 text-orange-700 border-orange-200">Walk-in</Badge>}
            {job.job_type === "Daily Wage" && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Daily Wage</Badge>}
            {(job.urgency === "urgent" || job.urgency === "immediate") && (
              <Badge className="bg-red-50 text-red-700 border-red-200">
                {job.urgency === "urgent" ? "Urgent Hiring" : "Immediate Hiring"}
              </Badge>
            )}
            {job.fresher_option === "freshers_can_apply" && <Badge className="bg-green-50 text-green-700 border-green-200">Freshers can apply</Badge>}
            {job.fresher_option === "freshers_only" && <Badge className="bg-green-50 text-green-700 border-green-200">Only freshers can apply</Badge>}
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-3" style={{fontFamily:"'Plus Jakarta Sans',sans-serif"}}>{job.title}</h1>
          <p className="text-slate-600 flex items-center gap-1 mt-1"><Building2 className="w-4 h-4" /> {job.business_name}</p>
          {job.company_reviews_count > 0 && (
            <div className="mt-1"><StarRating value={job.company_rating || 0} count={job.company_reviews_count} size="w-3.5 h-3.5" /></div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
            <div><div className="text-xs text-slate-500">Location</div><div className="font-medium flex items-center gap-1"><MapPin className="w-4 h-4 text-slate-400" />{job.city}</div></div>
            <div><div className="text-xs text-slate-500">Salary</div><div className="font-medium flex items-center gap-1"><IndianRupee className="w-4 h-4 text-slate-400" />{salary}</div></div>
            <div><div className="text-xs text-slate-500">Experience</div><div className="font-medium">{job.experience || "Any"}</div></div>
            <div><div className="text-xs text-slate-500">Vacancies</div><div className="font-medium flex items-center gap-1"><Users className="w-4 h-4 text-slate-400" />{job.vacancies}</div></div>
            {job.education && (
              <div><div className="text-xs text-slate-500">Minimum Education</div><div className="font-medium">{job.education}</div></div>
            )}
          </div>

          <section className="mt-8">
            <h2 className="text-lg font-semibold mb-2">Job Description</h2>
            <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{job.description}</p>
          </section>
          {job.requirements && (
            <section className="mt-6">
              <h2 className="text-lg font-semibold mb-2">Requirements</h2>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{job.requirements}</p>
            </section>
          )}
          {job.benefits?.length > 0 && (
            <section className="mt-6">
              <h2 className="text-lg font-semibold mb-2">Benefits</h2>
              <div className="flex flex-wrap gap-2">
                {job.benefits.map(b => <Badge key={b} variant="outline">{b}</Badge>)}
              </div>
            </section>
          )}
          {job.working_hours && (
            <section className="mt-6">
              <h2 className="text-lg font-semibold mb-2">Working Hours</h2>
              <p className="text-slate-700">{job.working_hours}</p>
            </section>
          )}
          {job.walkin_address && (
            <section className="mt-6">
              <h2 className="text-lg font-semibold mb-2">Walk-in Address</h2>
              <p className="text-slate-700">{job.walkin_address}</p>
            </section>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {whatsappLink ? (
              <a href={whatsappLink} target="_blank" rel="noreferrer">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="whatsapp-apply-btn">
                  <MessageCircle className="w-4 h-4 mr-2" /> Apply on WhatsApp
                </Button>
              </a>
            ) : applied ? (
              <Button disabled className="bg-green-600 hover:bg-green-600 text-white opacity-100 cursor-default" data-testid="applied-btn">
                ✓ Applied · <span className="ml-1.5 text-xs bg-white/20 px-2 py-0.5 rounded-full">{appliedStatus.replace("_", " ")}</span>
              </Button>
            ) : (
              <Button className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" onClick={() => user ? setOpen(true) : navigate("/login")} data-testid="apply-btn">
                Apply Now
              </Button>
            )}
            <Button variant="outline" onClick={save} data-testid="save-btn"><Bookmark className="w-4 h-4 mr-2" /> Save</Button>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); }} data-testid="share-btn">
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
            <Button variant="ghost" onClick={() => user ? setReportOpen(true) : navigate("/login")} data-testid="report-btn" className="text-red-600">
              <Flag className="w-4 h-4 mr-2" /> Report
            </Button>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900">About the employer</h3>
            <p className="mt-2 text-slate-700 font-medium">{job.business_name}</p>
            <p className="text-sm text-slate-500">{job.business_type}</p>
            {job.company_id && (
              <Link to={`/company/${job.company_id}`} className="text-[#1D6FF2] text-sm font-medium mt-2 inline-block" data-testid="view-company-link">
                View Company Profile →
              </Link>
            )}
            {job.contact_person && <p className="text-sm text-slate-600 mt-2">Contact: {job.contact_person}</p>}
            <p className="text-xs text-slate-400 mt-3 flex items-center gap-1"><Calendar className="w-3 h-3" /> Posted {(job.created_at || "").slice(0,10)}</p>
          </div>

          {companyJobs.length > 0 && (
            <div data-testid="other-company-jobs">
              <h3 className="font-semibold text-slate-900 mb-3">More from {job.business_name}</h3>
              <div className="space-y-3">
                {companyJobs.slice(0, 4).map(j => <JobCard key={j.id} job={j} />)}
              </div>
            </div>
          )}

          {job.similar?.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Similar jobs</h3>
              <div className="space-y-3">
                {job.similar.map(j => <JobCard key={j.id} job={j} />)}
              </div>
            </div>
          )}
        </aside>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply to {job.title}</DialogTitle>
            <DialogDescription>Add a short message to the employer (optional).</DialogDescription>
          </DialogHeader>
          <Textarea value={cover} onChange={e => setCover(e.target.value)} rows={5}
            placeholder="Why are you a great fit for this role?" data-testid="cover-letter-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={apply} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="confirm-apply-btn">Submit Application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Report this job</DialogTitle></DialogHeader>
          <Textarea value={reportReason} onChange={e => setReportReason(e.target.value)} placeholder="Reason..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submitReport}>Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
